import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { client, APIError } from "@/api"

interface AffiliatesQueryParams {
  page?: number
  pageSize?: number
  search?: string
  status?: "active" | "inactive"
  sortBy?: "name" | "created_at" | "totalOwedCents"
  sortOrder?: "asc" | "desc"
}

export interface Affiliate {
  public_id: string
  name: string
  contact_email: string
  payout_email: string
  ref_code: string
  commission_percent: number
  status: "active" | "inactive"
  admin_notes: string | null
  // Program terms; see the admin affiliate statements routes in the backend.
  commission_model: "first_payment" | "first_year_net"
  commission_window_months: number
  program_starts_at: string | null
  program_ends_at: string | null
  statement_cadence: "none" | "monthly" | "quarterly"
  statement_auto_send: boolean
  statement_recipients: string[] | null
  created_at: string
  updated_at: string
  totalReferred: number
  totalOwedCents: number
  referralUrl: string
}

interface Pagination {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

interface AffiliatesResponse {
  affiliates: Affiliate[]
  pagination: Pagination
}

export function useAffiliates(params: AffiliatesQueryParams = {}) {
  const {
    page = 1,
    pageSize = 20,
    search = "",
    status,
    sortBy = "created_at",
    sortOrder = "desc",
  } = params

  return useQuery<AffiliatesResponse>({
    queryKey: ["affiliates", page, pageSize, search, status, sortBy, sortOrder],
    queryFn: async () => {
      const response = await client.admin.affiliates.$get({
        query: {
          page,
          pageSize,
          search: search || undefined,
          status,
          sortBy,
          sortOrder,
        },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch affiliates")
      }
      return response.json()
    },
  })
}

export type CreateAffiliatePayload = {
  name: string
  contactEmail: string
  payoutEmail: string
  refCode: string
  commissionPercent: number
  adminNotes?: string
}

export function useCreateAffiliate() {
  const qc = useQueryClient()
  return useMutation<{ affiliate: Affiliate }, APIError, CreateAffiliatePayload>({
    mutationFn: async (payload) => {
      const res = await client.admin.affiliates.$post({ json: payload })
      if (!res.ok) {
        const err = (await res.clone().json().catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "AFFILIATE_CREATE_FAILED",
          new Error(err.message || "Failed to create affiliate")
        )
      }
      return (await res.json()) as { affiliate: Affiliate }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["affiliates"] })
    },
  })
}

// ref_code is deliberately absent, it's immutable after creation (see the backend
// route's own comment); use useAddAffiliateRefCode for an additional link variant.
export type UpdateAffiliatePayload = {
  publicId: string
  name?: string
  contactEmail?: string
  payoutEmail?: string
  commissionPercent?: number
  status?: "active" | "inactive"
  adminNotes?: string
  commissionModel?: "first_payment" | "first_year_net"
  commissionWindowMonths?: number
  programStartsAt?: string | null
  programEndsAt?: string | null
  statementCadence?: "none" | "monthly" | "quarterly"
  statementAutoSend?: boolean
  statementRecipients?: string[] | null
}

export function useUpdateAffiliate() {
  const qc = useQueryClient()
  return useMutation<{ affiliate: Affiliate }, APIError, UpdateAffiliatePayload>({
    mutationFn: async ({ publicId, ...payload }) => {
      const res = await client.admin.affiliates[":publicId"].$patch({
        param: { publicId },
        json: payload,
      })
      if (!res.ok) {
        const err = (await res.clone().json().catch(() => ({}))) as any
        throw new APIError(
          res.status,
          err.code || "AFFILIATE_UPDATE_FAILED",
          new Error(err.message || "Failed to update affiliate")
        )
      }
      return (await res.json()) as { affiliate: Affiliate }
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["affiliates"] })
      qc.invalidateQueries({ queryKey: ["affiliate", variables.publicId] })
    },
  })
}
